import { useState, useEffect } from 'react';

/**
 * Detects "mobile-like" devices based on input capabilities rather than viewport size.
 *
 * "Mobile-like" = touch-first / soft-keyboard-first environment.
 * Uses input capabilities (pointer/hover) + UA hints when available.
 *
 * Fails safe: if we're unsure, classify as mobile-like to avoid accidental sends.
 */
export function useIsMobileLike() {
  const getInitial = () => {
    // SSR guard
    if (typeof window === 'undefined') return false;

    // Prefer input capabilities (more reliable than viewport width).
    const mm =
      typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null;
    const pointerCoarse = mm ? mm('(pointer: coarse)').matches : false;
    const hoverNone = mm ? mm('(hover: none)').matches : false;

    // Chromium UA hint; absent on Safari currently.
    const uaMobile =
      typeof navigator !== 'undefined' ? (navigator.userAgentData?.mobile ?? false) : false;

    return uaMobile || pointerCoarse || hoverNone;
  };

  // Initialize from capabilities immediately to avoid "desktop" flashes on touch devices.
  const [isMobileLike, setIsMobileLike] = useState(getInitial);

  useEffect(() => {
    // SSR guard
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mqPointer = window.matchMedia('(pointer: coarse)');
    const mqHover = window.matchMedia('(hover: none)');

    const update = () => {
      const uaMobile = navigator.userAgentData?.mobile ?? false;
      setIsMobileLike(uaMobile || mqPointer.matches || mqHover.matches);
    };

    update();

    // Support older Safari (MediaQueryList.addListener/removeListener).
    const add = (mq) =>
      mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    const remove = (mq) =>
      mq.removeEventListener
        ? mq.removeEventListener('change', update)
        : mq.removeListener(update);

    add(mqPointer);
    add(mqHover);

    return () => {
      remove(mqPointer);
      remove(mqHover);
    };
  }, []);

  return isMobileLike;
}
